import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { BarChart3, Users, Mail, Settings, TrendingUp, Send, Check, X, FileText, Radio, Target, Plus, Trash2, Play, RefreshCw, Pencil, ThumbsUp, ThumbsDown, ExternalLink, Loader2, BookOpen, Gift, Copy, Share2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const hFont = { fontFamily: "'Manrope', sans-serif" };

/* ══════════════════════════════════════
   TAB: DASHBOARD
   ══════════════════════════════════════ */
function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/gtm/dashboard').then(r => setData(r.data)).catch(console.error); }, []);
  if (!data) return <p className="text-zinc-500 text-sm p-8">Loading...</p>;

  const cards = [
    { label: 'Total Leads', value: data.total_leads, color: 'text-white' },
    { label: 'This Week', value: data.leads_this_week, color: 'text-[#D94E18]' },
    { label: 'Activated', value: data.activated, color: 'text-emerald-400' },
    { label: 'Converted', value: data.converted, color: 'text-blue-400' },
    { label: 'Content Queue', value: data.content_in_queue, color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-6" data-testid="gtm-dashboard-tab">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{c.label}</p>
            <p className={`text-2xl font-extrabold mt-1 ${c.color}`} style={hFont}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <p className="text-sm font-semibold text-zinc-300 mb-3">Leads by Source (30d)</p>
          {data.source_breakdown.length === 0 && <p className="text-xs text-zinc-600">No data yet</p>}
          {data.source_breakdown.map(s => (
            <div key={s.source} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-zinc-400">{s.source}</span>
              <span className="text-sm font-bold text-white">{s.count}</span>
            </div>
          ))}
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <p className="text-sm font-semibold text-zinc-300 mb-3">Email Sequence</p>
          {[{ label: 'Day 0 sent', val: data.email_stats.day0_sent }, { label: 'Day 3 sent', val: data.email_stats.day3_sent }, { label: 'Day 7 sent', val: data.email_stats.day7_sent }].map(e => (
            <div key={e.label} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-zinc-400">{e.label}</span>
              <span className="text-sm font-bold text-white">{e.val}</span>
            </div>
          ))}
          <p className="text-xs text-zinc-600 mt-3">Last feedback run: {data.last_feedback_run || 'Never'}</p>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: LEADS
   ══════════════════════════════════════ */
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ status: '', source: '' });
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (filter.status) p.set('status', filter.status);
    if (filter.source) p.set('source', filter.source);
    const r = await api.get(`/gtm/leads?${p.toString()}`);
    setLeads(r.data.leads); setTotal(r.data.total);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const saveNotes = async (email) => {
    await api.patch(`/gtm/leads/${email}`, { notes });
    load(); setSelected(null);
  };

  return (
    <div className="space-y-4" data-testid="gtm-leads-tab">
      <div className="flex items-center gap-3">
        <select value={filter.status} onChange={e => setFilter(f => ({...f, status: e.target.value}))} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-300">
          <option value="">All statuses</option>
          {["new","activated","converted","churned"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.source} onChange={e => setFilter(f => ({...f, source: e.target.value}))} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-300">
          <option value="">All sources</option>
          {["calculator","demo","health_check","referral","organic","reddit"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-xs text-zinc-500 ml-auto">{total} leads</span>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-zinc-800">
            {['Email','Name','Source','Status','D0','D3','D7'].map(h => <th key={h} className="px-4 py-3 text-left text-[11px] text-zinc-500 uppercase">{h}</th>)}
          </tr></thead>
          <tbody>
            {leads.map(l => (
              <tr key={l.email} onClick={() => { setSelected(l); setNotes(l.notes || ''); }} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer">
                <td className="px-4 py-3 text-sm text-zinc-300">{l.email}</td>
                <td className="px-4 py-3 text-sm text-zinc-400">{l.name || '—'}</td>
                <td className="px-4 py-3"><Badge className="text-[10px] bg-zinc-800 text-zinc-400">{l.source}</Badge></td>
                <td className="px-4 py-3"><Badge className={`text-[10px] ${l.status === 'converted' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-zinc-800 text-zinc-400'}`}>{l.status}</Badge></td>
                <td className="px-4 py-3 text-center">{l.email_day0_sent_at ? <Check className="w-3.5 h-3.5 text-emerald-400 mx-auto" /> : <X className="w-3.5 h-3.5 text-zinc-700 mx-auto" />}</td>
                <td className="px-4 py-3 text-center">{l.email_day3_sent_at ? <Check className="w-3.5 h-3.5 text-emerald-400 mx-auto" /> : <X className="w-3.5 h-3.5 text-zinc-700 mx-auto" />}</td>
                <td className="px-4 py-3 text-center">{l.email_day7_sent_at ? <Check className="w-3.5 h-3.5 text-emerald-400 mx-auto" /> : <X className="w-3.5 h-3.5 text-zinc-700 mx-auto" />}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-600">No leads yet</td></tr>}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between"><h3 className="text-sm font-bold text-white">{selected.email}</h3><button onClick={() => setSelected(null)}><X className="w-4 h-4 text-zinc-500" /></button></div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300 resize-none" placeholder="Add notes..." />
          <Button onClick={() => saveNotes(selected.email)} size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white">Save Notes</Button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: CONTENT QUEUE
   ══════════════════════════════════════ */
function ContentTab() {
  const [content, setContent] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  const load = async () => { const r = await api.get('/gtm/content'); setContent(r.data.content); };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try { await api.post('/gtm/jobs/generate-content'); await load(); } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const approve = async (id) => { await api.post(`/gtm/content/${id}/approve`); load(); };
  const reject = async (id) => { await api.post(`/gtm/content/${id}/reject`); load(); };
  const postIt = async (id) => {
    try { await api.post(`/gtm/content/${id}/post`); load(); } catch { alert('Reddit post failed — check credentials'); }
  };
  const saveEdit = async (id) => { await api.patch(`/gtm/content/${id}`, editData); setEditing(null); load(); };

  const columns = [
    { key: 'draft', label: 'Drafts', color: 'text-amber-400' },
    { key: 'approved', label: 'Approved', color: 'text-blue-400' },
    { key: 'posted', label: 'Posted', color: 'text-emerald-400' },
  ];

  const channelIcon = (ch) => ch === 'reddit' ? 'R' : 'in';

  return (
    <div className="space-y-4" data-testid="gtm-content-tab">
      <div className="flex items-center gap-3">
        <Button onClick={generate} disabled={generating} className="bg-[#D94E18] hover:bg-[#C2410C] text-white">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Generating...</> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Generate Content</>}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {columns.map(col => (
          <div key={col.key}>
            <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${col.color}`}>{col.label} ({content.filter(c => c.status === col.key).length})</p>
            <div className="space-y-3">
              {content.filter(c => c.status === col.key).map(item => (
                <div key={item.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${item.channel === 'reddit' ? 'bg-orange-900/50 text-orange-400' : 'bg-blue-900/50 text-blue-400'}`}>
                      {channelIcon(item.channel)}
                    </span>
                    {item.subreddit && <span className="text-[10px] text-zinc-500">r/{item.subreddit}</span>}
                    <Badge className="text-[10px] bg-zinc-800 text-zinc-500 ml-auto">{item.generated_by}</Badge>
                  </div>
                  {editing === item.id ? (
                    <div className="space-y-2">
                      <Input value={editData.title || ''} onChange={e => setEditData(d => ({...d, title: e.target.value}))}
                        className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm" placeholder="Title" />
                      <textarea value={editData.body || ''} onChange={e => setEditData(d => ({...d, body: e.target.value}))}
                        rows={4} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300 resize-none" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit(item.id)} className="bg-[#D94E18] text-white text-xs">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="border-zinc-700 text-zinc-400 text-xs">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-white line-clamp-1">{item.title}</p>
                      <p className="text-xs text-zinc-400 mt-1 line-clamp-3">{item.body}</p>
                    </>
                  )}
                  {col.key === 'draft' && editing !== item.id && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => { setEditing(item.id); setEditData({ title: item.title, body: item.body }); }} className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                      <button onClick={() => approve(item.id)} className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> Approve</button>
                      <button onClick={() => reject(item.id)} className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> Reject</button>
                    </div>
                  )}
                  {col.key === 'approved' && (
                    <div className="flex gap-2 mt-3">
                      {item.channel === 'reddit' && (
                        <button onClick={() => postIt(item.id)} className="text-[10px] text-orange-400 hover:text-orange-300 flex items-center gap-1"><Play className="w-3 h-3" /> Post to Reddit</button>
                      )}
                      {item.channel === 'linkedin' && (
                        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=https://www.kayaworld.ai&title=${encodeURIComponent(item.title)}`}
                          target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Share on LinkedIn</a>
                      )}
                    </div>
                  )}
                  {col.key === 'posted' && item.posted_at && (
                    <p className="text-[10px] text-zinc-600 mt-2">Posted {new Date(item.posted_at).toLocaleDateString()}</p>
                  )}
                </div>
              ))}
              {content.filter(c => c.status === col.key).length === 0 && (
                <p className="text-xs text-zinc-700 text-center py-4">Empty</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: MARKET SIGNALS
   ══════════════════════════════════════ */
function SignalsTab() {
  const [signals, setSignals] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    const p = filter ? `?signal_type=${filter}` : '';
    const r = await api.get(`/gtm/signals${p}`);
    setSignals(r.data.signals); setTotal(r.data.total);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const runMonitor = async () => {
    setRunning(true);
    try { await api.post('/gtm/jobs/reddit-monitor'); await load(); } catch (e) { console.error(e); }
    setRunning(false);
  };

  const runFeedback = async () => {
    setRunning(true);
    try { await api.post('/gtm/jobs/feedback-loop'); await load(); } catch (e) { console.error(e); }
    setRunning(false);
  };

  const typeColor = (t) => {
    switch(t) {
      case 'pain_mention': return 'bg-red-900/50 text-red-400';
      case 'recommendation_request': return 'bg-blue-900/50 text-blue-400';
      case 'competitor_frustration': return 'bg-amber-900/50 text-amber-400';
      case 'feature_request': return 'bg-violet-900/50 text-violet-400';
      default: return 'bg-zinc-800 text-zinc-400';
    }
  };

  return (
    <div className="space-y-4" data-testid="gtm-signals-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={filter} onChange={e => setFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-300">
          <option value="">All types</option>
          {["pain_mention","recommendation_request","competitor_frustration","feature_request","general"].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
        </select>
        <span className="text-xs text-zinc-500">{total} signals</span>
        <div className="ml-auto flex gap-2">
          <Button onClick={runMonitor} disabled={running} size="sm" variant="outline" className="border-zinc-700 text-zinc-300 text-xs">
            <Radio className="w-3 h-3 mr-1" /> Scan Reddit Now
          </Button>
          <Button onClick={runFeedback} disabled={running} size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Run Feedback Loop
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {signals.map((s) => (
          <div key={s.id || s.reddit_id || `sig-${s.channel_detail}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] text-zinc-500">{s.channel_detail}</span>
              <Badge className={`text-[10px] ${typeColor(s.signal_type)}`}>{(s.signal_type || 'general').replace('_', ' ')}</Badge>
              <span className="text-[10px] text-zinc-600 ml-auto">{s.upvotes} upvotes</span>
              {s.processed && <Badge className="text-[10px] bg-emerald-900/30 text-emerald-500">processed</Badge>}
            </div>
            <p className="text-sm text-zinc-300 line-clamp-2">{s.content}</p>
            {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#D94E18] hover:underline mt-1 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View on Reddit</a>}
          </div>
        ))}
        {signals.length === 0 && <p className="text-sm text-zinc-600 text-center py-8">No signals yet — run a Reddit scan to populate</p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: REDDIT MONITORS (Configurable)
   ══════════════════════════════════════ */
function MonitorsTab() {
  const [monitors, setMonitors] = useState([]);
  const [newSub, setNewSub] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editKeywords, setEditKeywords] = useState('');

  const load = async () => { const r = await api.get('/gtm/monitors'); setMonitors(r.data.monitors); };
  useEffect(() => { load(); }, []);

  const addMonitor = async () => {
    if (!newSub.trim()) return;
    const kw = newKeywords.split(',').map(k => k.trim()).filter(Boolean);
    await api.post('/gtm/monitors', { subreddit: newSub.trim(), keywords: kw });
    setNewSub(''); setNewKeywords(''); load();
  };

  const saveKeywords = async (id) => {
    const kw = editKeywords.split(',').map(k => k.trim()).filter(Boolean);
    await api.patch(`/gtm/monitors/${id}`, { keywords: kw });
    setEditingId(null); load();
  };

  const toggleActive = async (id, current) => {
    await api.patch(`/gtm/monitors/${id}`, { is_active: !current });
    load();
  };

  const deleteMonitor = async (id) => {
    await api.delete(`/gtm/monitors/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="gtm-monitors-tab">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <p className="text-sm font-bold text-white mb-3">Add New Monitor</p>
        <div className="flex gap-3 flex-wrap">
          <Input value={newSub} onChange={e => setNewSub(e.target.value)} placeholder="Subreddit (e.g. smallbusiness)"
            className="bg-zinc-800 border-zinc-700 text-zinc-300 placeholder-zinc-500 w-48" />
          <Input value={newKeywords} onChange={e => setNewKeywords(e.target.value)} placeholder="Keywords (comma separated)"
            className="bg-zinc-800 border-zinc-700 text-zinc-300 placeholder-zinc-500 flex-1" />
          <Button onClick={addMonitor} size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      </div>

      <div className="space-y-3">
        {monitors.map(m => (
          <div key={m.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-white">r/{m.subreddit}</span>
                <Badge className={`text-[10px] ${m.is_active ? 'bg-emerald-900/50 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>
                  {m.is_active ? 'Active' : 'Paused'}
                </Badge>
                {m.last_checked_at && <span className="text-[10px] text-zinc-600">Last scanned: {new Date(m.last_checked_at).toLocaleString()}</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleActive(m.id, m.is_active)} className="text-[10px] text-zinc-400 hover:text-white">{m.is_active ? 'Pause' : 'Resume'}</button>
                <button onClick={() => deleteMonitor(m.id)} className="text-[10px] text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>

            {editingId === m.id ? (
              <div className="flex gap-2 mt-2">
                <Input value={editKeywords} onChange={e => setEditKeywords(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm flex-1" placeholder="Keywords (comma separated)" />
                <Button size="sm" onClick={() => saveKeywords(m.id)} className="bg-[#D94E18] text-white text-xs">Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-zinc-700 text-zinc-400 text-xs">Cancel</Button>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <div className="flex flex-wrap gap-1.5">
                  {m.keywords.map((kw) => (
                    <span key={kw} className="text-[11px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full">{kw}</span>
                  ))}
                </div>
                <button onClick={() => { setEditingId(m.id); setEditKeywords(m.keywords.join(', ')); }}
                  className="text-[10px] text-[#D94E18] hover:underline flex items-center gap-1 flex-shrink-0 ml-auto"><Pencil className="w-3 h-3" /> Edit</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: POSITIONING
   ══════════════════════════════════════ */
function PositioningTab() {
  const [pos, setPos] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/gtm/positioning').then(r => setPos(r.data)).catch(console.error); }, []);

  const save = async () => {
    setSaving(true);
    await api.put('/gtm/positioning', pos);
    setSaving(false);
  };

  if (!pos) return <p className="text-zinc-500 text-sm p-8">Loading...</p>;

  const fields = [
    { key: 'one_liner', label: 'One-Liner' },
    { key: 'target_user', label: 'Target User' },
    { key: 'pain_statement', label: 'Pain Statement' },
    { key: 'solution_hook', label: 'Solution Hook' },
    { key: 'hero_statement', label: 'Hero Statement' },
    { key: 'social_proof_hook', label: 'Social Proof Hook' },
    { key: 'cta_text', label: 'CTA Text' },
  ];

  const listFields = [
    { key: 'icp_titles', label: 'ICP Titles' },
    { key: 'icp_industries', label: 'ICP Industries' },
    { key: 'competitors', label: 'Competitors' },
  ];

  return (
    <div className="space-y-4 max-w-2xl" data-testid="gtm-positioning-tab">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-xs text-zinc-500 block mb-1">{f.label}</label>
            <textarea value={pos[f.key] || ''} onChange={e => setPos(p => ({...p, [f.key]: e.target.value}))} rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300 resize-none" />
          </div>
        ))}
        {listFields.map(f => (
          <div key={f.key}>
            <label className="text-xs text-zinc-500 block mb-1">{f.label} (comma separated)</label>
            <Input value={(pos[f.key] || []).join(', ')} onChange={e => setPos(p => ({...p, [f.key]: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
              className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm" />
          </div>
        ))}
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Company Size</label>
          <Input value={pos.icp_company_size || ''} onChange={e => setPos(p => ({...p, icp_company_size: e.target.value}))}
            className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#D94E18] hover:bg-[#C2410C] text-white">
        {saving ? 'Saving...' : 'Save Positioning'}
      </Button>
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: EMAILS
   ══════════════════════════════════════ */
function EmailTab() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testSent, setTestSent] = useState(false);

  useEffect(() => { api.get('/gtm/email-templates').then(r => setTemplates(r.data.templates)).catch(console.error); }, []);

  const saveTemplate = async (day) => {
    const t = templates.find(t => t.sequence_day === day);
    await api.put(`/gtm/email-templates/${day}`, { subject: t.subject, body_text: t.body_text, is_active: t.is_active });
    setEditing(null);
  };

  const sendTest = async () => {
    if (!testEmail) return;
    await api.post('/gtm/settings/test-email', { email: testEmail });
    setTestSent(true); setTimeout(() => setTestSent(false), 3000);
  };

  const dayLabels = { 0: 'Welcome (Day 0)', 3: 'Nudge (Day 3)', 7: 'Final (Day 7)' };

  return (
    <div className="space-y-4" data-testid="gtm-email-tab">
      <div className="flex items-center gap-3">
        <Input type="email" placeholder="Send test email to..." value={testEmail} onChange={e => setTestEmail(e.target.value)}
          className="bg-zinc-900 border-zinc-800 text-zinc-300 placeholder-zinc-500 max-w-xs" />
        <Button onClick={sendTest} size="sm" variant="outline" className="border-zinc-700 text-zinc-300"><Send className="w-3 h-3 mr-1.5" /> {testSent ? 'Sent!' : 'Send Test'}</Button>
      </div>
      {templates.map(t => (
        <div key={t.sequence_day} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-white">{dayLabels[t.sequence_day]}</h3>
              <Badge className={`text-[10px] ${t.is_active ? 'bg-emerald-900/50 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>{t.is_active ? 'Active' : 'Inactive'}</Badge>
            </div>
            <button onClick={() => setEditing(editing === t.sequence_day ? null : t.sequence_day)} className="text-xs text-[#D94E18] hover:underline">{editing === t.sequence_day ? 'Cancel' : 'Edit'}</button>
          </div>
          {editing === t.sequence_day ? (
            <div className="space-y-3">
              <Input value={t.subject} onChange={e => setTemplates(ts => ts.map(x => x.sequence_day === t.sequence_day ? {...x, subject: e.target.value} : x))}
                className="bg-zinc-800 border-zinc-700 text-zinc-300" />
              <textarea value={t.body_text} onChange={e => setTemplates(ts => ts.map(x => x.sequence_day === t.sequence_day ? {...x, body_text: e.target.value} : x))}
                rows={4} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300 font-mono resize-none" />
              <Button onClick={() => saveTemplate(t.sequence_day)} size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white">Save</Button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-zinc-400">Subject: <span className="text-zinc-300">{t.subject}</span></p>
              <p className="text-xs text-zinc-600 mt-1 line-clamp-2">{t.body_text}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: SETTINGS
   ══════════════════════════════════════ */
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get('/gtm/settings').then(r => setSettings(r.data)).catch(console.error); }, []);
  if (!settings) return <p className="text-zinc-500 text-sm p-8">Loading...</p>;

  return (
    <div className="space-y-4 max-w-lg" data-testid="gtm-settings-tab">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-bold text-white">Configuration</h3>
        {[{ l: 'Sender Email', v: settings.sender_email }, { l: 'From Name', v: settings.from_name }, { l: 'App URL', v: settings.app_url }].map(r => (
          <div key={r.l}><label className="text-xs text-zinc-500">{r.l}</label><p className="text-sm text-zinc-300">{r.v || 'Not set'}</p></div>
        ))}
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white mb-2">System Status</h3>
        <div className="space-y-2 text-sm">
          {[
            { label: 'Email sequence', status: true, note: 'running hourly' },
            { label: 'Reddit monitor', status: true, note: 'daily at 2am UTC' },
            { label: 'Content generator', status: true, note: 'on-demand + weekly feedback loop' },
            { label: 'Feedback loop', status: true, note: 'Sunday midnight UTC' },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${s.status ? 'bg-emerald-500' : 'bg-zinc-600'}`} />
              <span className="text-zinc-400">{s.label}: {s.note}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: BLOG ARTICLES
   ══════════════════════════════════════ */
function BlogTab() {
  const [articles, setArticles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  const load = async () => { const r = await api.get('/gtm/blog'); setArticles(r.data.articles); };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try { await api.post('/gtm/jobs/generate-blog'); await load(); } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const toggleStatus = async (id, current) => {
    const newStatus = current === 'published' ? 'draft' : 'published';
    await api.put(`/gtm/blog/${id}`, { status: newStatus });
    load();
  };

  const deleteArticle = async (id) => {
    await api.delete(`/gtm/blog/${id}`);
    load();
  };

  const saveEdit = async (id) => {
    await api.put(`/gtm/blog/${id}`, editData);
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-4" data-testid="gtm-blog-tab">
      <div className="flex items-center gap-3">
        <Button onClick={generate} disabled={generating} className="bg-[#D94E18] hover:bg-[#C2410C] text-white">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Generating...</> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Generate Blog Articles</>}
        </Button>
        <span className="text-xs text-zinc-500">{articles.length} articles</span>
      </div>

      <div className="space-y-3">
        {articles.map(a => (
          <div key={a.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            {editing === a.id ? (
              <div className="space-y-3">
                <Input value={editData.title || ''} onChange={e => setEditData(d => ({...d, title: e.target.value}))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm" placeholder="Title" />
                <Input value={editData.category || ''} onChange={e => setEditData(d => ({...d, category: e.target.value}))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm" placeholder="Category" />
                <Input value={editData.excerpt || ''} onChange={e => setEditData(d => ({...d, excerpt: e.target.value}))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm" placeholder="Excerpt" />
                <textarea value={editData.content || ''} onChange={e => setEditData(d => ({...d, content: e.target.value}))}
                  rows={8} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300 font-mono resize-y" placeholder="Markdown content..." />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveEdit(a.id)} className="bg-[#D94E18] text-white text-xs">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="border-zinc-700 text-zinc-400 text-xs">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`text-[10px] ${a.status === 'published' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>
                    {a.status}
                  </Badge>
                  <Badge className="text-[10px] bg-zinc-800 text-zinc-400">{a.category}</Badge>
                  <span className="text-[10px] text-zinc-600">{a.read_time}</span>
                  {a.published_at && <span className="text-[10px] text-zinc-600 ml-auto">{new Date(a.published_at).toLocaleDateString()}</span>}
                </div>
                <p className="text-sm font-medium text-white">{a.title}</p>
                <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{a.excerpt}</p>
                <div className="flex gap-3 mt-3">
                  <button onClick={() => { setEditing(a.id); setEditData({ title: a.title, category: a.category, excerpt: a.excerpt, content: a.content }); }}
                    className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                  <button onClick={() => toggleStatus(a.id, a.status)}
                    className={`text-[10px] flex items-center gap-1 ${a.status === 'published' ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'}`}>
                    {a.status === 'published' ? <><X className="w-3 h-3" /> Unpublish</> : <><Check className="w-3 h-3" /> Publish</>}
                  </button>
                  {a.status === 'published' && (
                    <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] text-[#D94E18] hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View</a>
                  )}
                  <button onClick={() => deleteArticle(a.id)} className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1 ml-auto"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </>
            )}
          </div>
        ))}
        {articles.length === 0 && <p className="text-sm text-zinc-600 text-center py-8">No blog articles yet — generate some or publish from Content tab</p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   TAB: REFERRALS
   ══════════════════════════════════════ */
function ReferralsTab() {
  const [referrals, setReferrals] = useState([]);

  useEffect(() => { api.get('/gtm/referrals').then(r => setReferrals(r.data.referrals)).catch(console.error); }, []);

  const totalReferred = referrals.reduce((sum, r) => sum + (r.referrals_count || 0), 0);
  const totalConverted = referrals.reduce((sum, r) => sum + (r.referrals_converted || 0), 0);
  const totalRewards = referrals.reduce((sum, r) => sum + (r.reward_months_earned || 0), 0);

  return (
    <div className="space-y-4" data-testid="gtm-referrals-tab">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Active Referrers', value: referrals.length, color: 'text-white' },
          { label: 'Total Referred', value: totalReferred, color: 'text-[#D94E18]' },
          { label: 'Converted', value: totalConverted, color: 'text-emerald-400' },
        ].map(c => (
          <div key={c.label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{c.label}</p>
            <p className={`text-2xl font-extrabold mt-1 ${c.color}`} style={hFont}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <p className="text-sm font-bold text-white mb-1">Reward Structure</p>
        <p className="text-xs text-zinc-400">Each converted referral earns the referrer 1 free month at retail value. Total months earned: <span className="text-emerald-400 font-bold">{totalRewards}</span></p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-zinc-800">
            {['Email', 'Code', 'Referred', 'Converted', 'Months Earned', 'Created'].map(h => <th key={h} className="px-4 py-3 text-left text-[11px] text-zinc-500 uppercase">{h}</th>)}
          </tr></thead>
          <tbody>
            {referrals.map(r => (
              <tr key={r.code} className="border-b border-zinc-800/50">
                <td className="px-4 py-3 text-sm text-zinc-300">{r.email}</td>
                <td className="px-4 py-3"><code className="text-xs bg-zinc-800 text-[#D94E18] px-2 py-0.5 rounded">{r.code}</code></td>
                <td className="px-4 py-3 text-sm text-zinc-400 text-center">{r.referrals_count}</td>
                <td className="px-4 py-3 text-sm text-emerald-400 text-center">{r.referrals_converted}</td>
                <td className="px-4 py-3 text-sm text-amber-400 text-center">{r.reward_months_earned}</td>
                <td className="px-4 py-3 text-xs text-zinc-600">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
            {referrals.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-600">No referrals yet — codes are generated when users make their first upload</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN GROWTH ENGINE PAGE
   ══════════════════════════════════════ */
export default function GTMAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [hasAccess, setHasAccess] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.get('/gtm/check-access')
      .then(r => { if (!r.data.has_access) navigate('/dashboard'); else setHasAccess(true); })
      .catch(() => navigate('/login'));
  }, [user, navigate]);

  if (!hasAccess) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500">Checking access...</div>;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'blog', label: 'Blog', icon: BookOpen },
    { id: 'signals', label: 'Signals', icon: Radio },
    { id: 'monitors', label: 'Monitors', icon: Target },
    { id: 'referrals', label: 'Referrals', icon: Gift },
    { id: 'positioning', label: 'Positioning', icon: TrendingUp },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="gtm-admin-page">
      <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#D94E18]/10 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-[#D94E18]" />
          </div>
          <div>
            <h1 className="text-lg font-extrabold text-white" style={hFont}>Growth Engine</h1>
            <p className="text-[11px] text-zinc-500">kaya.ai growth engine</p>
          </div>
        </div>
        <button onClick={() => navigate('/dashboard')} className="text-xs text-zinc-500 hover:text-zinc-300">Back to app</button>
      </div>

      <div className="flex">
        <div className="w-48 border-r border-zinc-800 min-h-[calc(100vh-65px)] py-4 px-3 space-y-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                activeTab === t.id ? 'bg-zinc-800 text-white font-semibold' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900'
              }`}>
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 p-6 max-w-5xl">
          {activeTab === 'dashboard' && <DashboardTab />}
          {activeTab === 'leads' && <LeadsTab />}
          {activeTab === 'content' && <ContentTab />}
          {activeTab === 'blog' && <BlogTab />}
          {activeTab === 'signals' && <SignalsTab />}
          {activeTab === 'monitors' && <MonitorsTab />}
          {activeTab === 'referrals' && <ReferralsTab />}
          {activeTab === 'positioning' && <PositioningTab />}
          {activeTab === 'emails' && <EmailTab />}
          {activeTab === 'settings' && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}
